import { useEffect } from 'react';
import { useApi } from '@/api/provider';
import { lastUserActivity } from '@/session/activity';

/**
 * Tells the server which non-INBOX folder this tab has open (#67), so it is
 * polled every 30 s instead of every 120 s and its new mail reaches the open
 * list as `new_mail`.
 *
 * - `PUT …/folders/{id}/focus` when the folder opens, then every
 *   {@link FOCUS_HEARTBEAT_MS} while the tab is visible. The server holds a
 *   focus for 150 s, so a hidden or closed tab simply stops renewing it; there
 *   is no "unfocus" call.
 * - Never for the inbox: the server watches it live already (IDLE).
 * - **Not for an unattended tab.** After {@link FOCUS_IDLE_LIMIT_MS} without
 *   input (a desktop tab left open on a folder for days), the heartbeat stops;
 *   the next pointer or key input resumes it at once.
 * - Failures are ignored: the 120 s sweep is the fallback, and nothing a
 *   person does depends on this request.
 */
export const FOCUS_HEARTBEAT_MS = 60_000;
export const FOCUS_IDLE_LIMIT_MS = 2 * 60 * 60_000;

const RESUME_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

export function useFolderFocus(
  accountId: string | undefined,
  folderId: string | undefined,
  isInbox: boolean,
): void {
  const api = useApi();

  useEffect(() => {
    if (!accountId || !folderId || isInbox) return;
    let stopped = false;
    let paused = false;

    const visible = () =>
      typeof document === 'undefined' || document.visibilityState === 'visible';
    const attended = () => Date.now() - lastUserActivity() < FOCUS_IDLE_LIMIT_MS;

    const send = () => {
      if (stopped || !visible()) return;
      api.folders.focus(accountId, folderId).catch(() => {
        /* the 120 s sweep is the fallback */
      });
    };

    const resume = () => {
      if (!paused || stopped) return;
      paused = false;
      removeResume();
      send();
    };
    const removeResume = () => {
      for (const type of RESUME_EVENTS) window.removeEventListener(type, resume, true);
    };

    const tick = () => {
      if (stopped || !visible()) return;
      if (!attended()) {
        if (!paused) {
          paused = true;
          for (const type of RESUME_EVENTS) {
            window.addEventListener(type, resume, { capture: true, passive: true });
          }
        }
        return;
      }
      send();
    };

    const onVisibility = () => {
      // Back in view: renew at once rather than up to a minute later.
      if (visible()) tick();
    };

    send();
    const timer = setInterval(tick, FOCUS_HEARTBEAT_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearInterval(timer);
      removeResume();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [api, accountId, folderId, isInbox]);
}
